import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { categoriesData, productsData, businessInfo } from '../shopData';
import { ArrowLeft } from 'lucide-react';
import Ornament from '../components/Ornament';
import ImageLightbox from '../components/ImageLightbox';
import useDocumentTitle from '../hooks/useDocumentTitle';

const WA_ICON = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const CategoryPage = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const categoryData = categoriesData[categorySlug];
  const [zoomProduct, setZoomProduct] = useState(null);

  useDocumentTitle(
    categoryData
      ? `${categoryData.name} | Shri Kothari Jewellers`
      : 'Category Not Found | Shri Kothari Jewellers',
    categoryData
      ? `Browse our ${categoryData.name} collection — ${categoryData.description}. Inquire directly on WhatsApp.`
      : undefined
  );

  if (!categoryData) {
    return (
      <div className="lux-section" style={{ paddingTop: '7rem', minHeight: '100vh' }}>
        <div className="lux-container text-center">
          <h1 className="lux-title">Category Not Found</h1>
          <Link to="/shop" className="lux-btn-outline" data-testid="category-404-back">
            Return to Shop
          </Link>
        </div>
      </div>
    );
  }

  const allCategoryProducts = productsData.filter(p => p.category === categorySlug);

  return (
    <div className="lux-section" style={{ paddingTop: '6rem', minHeight: '100vh' }}>
      <div className="lux-container">
        {/* Breadcrumb */}
        <nav className="lux-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" className="lux-breadcrumb-link">Home</Link>
          <span className="lux-breadcrumb-sep">/</span>
          <Link to="/shop" className="lux-breadcrumb-link">Shop</Link>
          <span className="lux-breadcrumb-sep">/</span>
          <span className="lux-breadcrumb-current">{categoryData.name}</span>
        </nav>

        <button
          onClick={() => navigate(-1)}
          className="lux-back-btn"
          data-testid="category-back-btn"
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        {/* Category Header */}
        <div className="lux-header">
          <div className="lux-eyebrow">Category</div>
          <h1 className="lux-title">{categoryData.name}</h1>
          <Ornament />
          <p className="lux-subtitle">{categoryData.description}</p>
        </div>

        {/* Subcategories */}
        {categoryData.subcategories.map((subcat) => {
          const subcatProducts = allCategoryProducts.filter(p => p.subcategory === subcat.id);
          if (subcatProducts.length === 0) return null;

          return (
            <section key={subcat.id} className="lux-subcat-section">
              <div className="lux-subcat-header">
                <h2 className="lux-subcat-title">{subcat.name}</h2>
                <p className="lux-subcat-items">{subcat.items.join(' · ')}</p>
              </div>

              {/* Product Grid - centred flex layout so any number of items sits in middle */}
              <div className="lux-product-grid" data-testid={`subcat-grid-${subcat.id}`}>
                {subcatProducts.map((product) => (
                  <div
                    key={product.id}
                    className="lux-product-card"
                    data-testid={`product-card-${product.id}`}
                  >
                    <div
                      className="lux-product-image-wrap"
                      onClick={() => setZoomProduct(product)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setZoomProduct(product)}
                      data-testid={`product-image-zoom-${product.id}`}
                    >
                      <img
                        src={product.image}
                        alt={product.name}
                        className={`lux-product-image${product.fit === 'contain' ? ' lux-product-image-contain' : ''}`}
                        loading="lazy"
                      />
                    </div>
                    <div className="lux-product-info">
                      <h3 className="lux-product-name">{product.name}</h3>
                      <a
                        href={`https://wa.me/${businessInfo.whatsappNumber}?text=${encodeURIComponent(`Hi, I'm interested in ${product.name}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lux-product-wa-btn"
                        data-testid={`product-whatsapp-${product.id}`}
                      >
                        {WA_ICON}
                        <span>Inquire</span>
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* Per-subcat CTA — prominent outlined pill */}
              <div className="lux-subcat-cta">
                <a
                  href="https://wa.me/c/919893254712"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lux-btn-outline"
                  data-testid={`subcat-more-${subcat.id}`}
                >
                  {WA_ICON}
                  <span>See More {subcat.name} on WhatsApp</span>
                  <span>&rarr;</span>
                </a>
              </div>
            </section>
          );
        })}

        {/* Bottom ornament + full catalogue CTA */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <Ornament />
          <a
            href="https://wa.me/c/919893254712"
            target="_blank"
            rel="noopener noreferrer"
            className="lux-btn-outline"
            data-testid="category-full-catalogue-cta"
            style={{ marginTop: '0.5rem' }}
          >
            {WA_ICON}
            <span>Browse Full Catalogue</span>
            <span>&rarr;</span>
          </a>
        </div>
      </div>
      <ImageLightbox product={zoomProduct} onClose={() => setZoomProduct(null)} />
    </div>
  );
};

export default CategoryPage;
